/**
 * PAPPO — NotificationsContext
 * Contador global de notificações (mensagens não lidas + pendências + alertas).
 * Context porque a Navbar (e potencialmente a Sidebar) consomem o total.
 *
 * Degradação graciosa: getUnreadAlertCount() devolve 0 em falha; as contagens
 * de responsável só são buscadas para parent/admin.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import { listConversations } from '../services/conversations';
import { listContacts } from '../services/contacts';
import { listConnectionRequests } from '../services/parent';
import { getUnreadAlertCount } from '../services/alerts';
import type { Message } from '../types/message';

interface NotificationsValue {
  unreadMessages: number;
  pendingContacts: number;
  pendingConnections: number;
  unreadAlerts: number;
  total: number;
  /** Ressincroniza as contagens a partir da API (ex.: ao abrir/ler conversa) */
  refresh: () => Promise<void>;
  /** Informa qual a conversa aberta (para não contar mensagens já visíveis) */
  setActiveConversation: (id: number | null) => void;
}

const NotificationsContext = createContext<NotificationsValue | undefined>(
  undefined
);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { on } = useSocket();
  const currentUserId = user?.id ?? 0;
  const isParent = user?.role === 'parent' || user?.role === 'admin';

  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingContacts, setPendingContacts] = useState(0);
  const [pendingConnections, setPendingConnections] = useState(0);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  // Conversa atualmente aberta — mensagens nela são lidas na hora, não contam.
  const activeConvIdRef = useRef<number | null>(null);
  const setActiveConversation = useCallback((id: number | null) => {
    activeConvIdRef.current = id;
  }, []);

  const refresh = useCallback(async () => {
    const [convs, contacts] = await Promise.all([
      listConversations(),
      listContacts(),
    ]);
    if (convs.success && convs.data) {
      setUnreadMessages(
        convs.data.conversations.reduce(
          (sum, c) => sum + (c.unread_count ?? 0),
          0
        )
      );
    }
    if (contacts.success && contacts.data) {
      setPendingContacts(contacts.data.pending_incoming.length);
    }

    if (isParent) {
      const [reqs, alertsCount] = await Promise.all([
        listConnectionRequests(),
        getUnreadAlertCount(),
      ]);
      if (reqs.success && reqs.data) {
        setPendingConnections(reqs.data.requests.length);
      }
      setUnreadAlerts(alertsCount);
    } else {
      setPendingConnections(0);
      setUnreadAlerts(0);
    }
  }, [isParent]);

  useEffect(() => {
    if (currentUserId) refresh();
  }, [currentUserId, refresh]);

  /* Atualizações ao vivo */
  useEffect(() => {
    const offMsg = on('receive_message', (...args) => {
      const msg = args[0] as Message;
      // Ignora as próprias mensagens e as da conversa aberta (já lidas na hora).
      if (
        msg.sender_id !== currentUserId &&
        msg.conversation_id !== activeConvIdRef.current
      ) {
        setUnreadMessages((n) => n + 1);
      }
    });
    const offConv = on('conversation:new', () => {
      refresh();
    });
    const offAlert = on('alert:new', () => setUnreadAlerts((n) => n + 1));
    return () => {
      offMsg();
      offConv();
      offAlert();
    };
  }, [on, currentUserId, refresh]);

  const total =
    unreadMessages + pendingContacts + pendingConnections + unreadAlerts;

  return (
    <NotificationsContext.Provider
      value={{
        unreadMessages,
        pendingContacts,
        pendingConnections,
        unreadAlerts,
        total,
        refresh,
        setActiveConversation,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      'useNotifications deve ser usado dentro de NotificationsProvider'
    );
  }
  return ctx;
}
